// PERFECT 10 — admin tooling hook.
//
// Founder-only. Backs the inline admin strip in the Perfect 10 tab:
//   - regenerate()      → invokes perfect10-engine with trigger='admin'
//   - loadLogs()        → get_perfect10_generation_logs (scoring transparency)
//   - excludeLeg()      → add_perfect10_exclusion
//   - loadExclusions()  → list_perfect10_exclusions
//   - removeExclusion() → remove_perfect10_exclusion
//
// All server RPCs re-check is_strikza_admin(); this hook only drives UX.

import { useCallback, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface Perfect10RawScore {
  source_leg_id: string;
  source_tier: string;
  match_name: string;
  market: string;
  pick: string;
  odds: number;
  conviction_score: number;
  base: number;
  form: number;
  h2h: number;
  depth: number;
  recency: number;
  value: number;
}

export interface Perfect10GenLog {
  id: string;
  slip_id: string | null;
  week_number: number | null;
  generated_at: string | null;
  triggered_by: string | null;
  total_legs_considered: number | null;
  total_legs_after_dedup: number | null;
  legs_selected: number | null;
  duration_ms: number | null;
  error_message: string | null;
  raw_scores: Perfect10RawScore[];
}

export interface Perfect10Exclusion {
  id: string;
  source_leg_id: string;
  source_tier: string | null;
  fixture_id: string | null;
  match_name: string | null;
  reason: string | null;
  expires_at: string | null;
  created_at: string | null;
}

interface RegenResult {
  ok: boolean;
  status?: string;
  reason?: string;
  avg_conviction?: number;
  legs_selected?: number;
  error?: string;
}

export function usePerfect10Admin() {
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState<Perfect10GenLog[]>([]);
  const [exclusions, setExclusions] = useState<Perfect10Exclusion[]>([]);
  const [lastResult, setLastResult] = useState<RegenResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rpc = useCallback(
    async (fn: string, args?: Record<string, unknown>) => {
      const client = getBrowserClient();
      // .call(client, …) preserves `this` — a bare `client.rpc` reference loses
      // it and trips the client's internal `this.rest` access.
      return (client.rpc as unknown as (
        f: string,
        a?: Record<string, unknown>,
      ) => Promise<{ data: Record<string, unknown> | null; error: { message: string } | null }>)
        .call(client, fn, args);
    },
    [],
  );

  const regenerate = useCallback(async (): Promise<RegenResult> => {
    setBusy(true);
    setError(null);
    try {
      const client = getBrowserClient();
      const { data, error: invokeErr } = await client.functions.invoke("perfect10-engine", {
        body: { trigger: "admin" },
      });
      if (invokeErr) {
        const r: RegenResult = { ok: false, error: invokeErr.message };
        setLastResult(r);
        setError(invokeErr.message);
        return r;
      }
      const r = (data ?? { ok: false }) as RegenResult;
      setLastResult(r);
      return r;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      const r: RegenResult = { ok: false, error: msg };
      setLastResult(r);
      return r;
    } finally {
      setBusy(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    const { data, error: e } = await rpc("get_perfect10_generation_logs", { p_limit: 20 });
    if (e) { setError(e.message); return; }
    const rows = Array.isArray((data as Record<string, unknown>)?.logs)
      ? ((data as Record<string, unknown>).logs as Record<string, unknown>[])
      : [];
    setLogs(rows.map((r) => ({
      id: String(r.id ?? ""),
      slip_id: typeof r.slip_id === "string" ? r.slip_id : null,
      week_number: typeof r.week_number === "number" ? r.week_number : null,
      generated_at: typeof r.generated_at === "string" ? r.generated_at : null,
      triggered_by: typeof r.triggered_by === "string" ? r.triggered_by : null,
      total_legs_considered: typeof r.total_legs_considered === "number" ? r.total_legs_considered : null,
      total_legs_after_dedup: typeof r.total_legs_after_dedup === "number" ? r.total_legs_after_dedup : null,
      legs_selected: typeof r.legs_selected === "number" ? r.legs_selected : null,
      duration_ms: typeof r.duration_ms === "number" ? r.duration_ms : null,
      error_message: typeof r.error_message === "string" ? r.error_message : null,
      raw_scores: Array.isArray(r.raw_scores) ? (r.raw_scores as Perfect10RawScore[]) : [],
    })));
  }, [rpc]);

  const loadExclusions = useCallback(async () => {
    const { data, error: e } = await rpc("list_perfect10_exclusions");
    if (e) { setError(e.message); return; }
    const rows = Array.isArray((data as Record<string, unknown>)?.exclusions)
      ? ((data as Record<string, unknown>).exclusions as Record<string, unknown>[])
      : [];
    setExclusions(rows.map((r) => ({
      id: String(r.id ?? ""),
      source_leg_id: String(r.source_leg_id ?? ""),
      source_tier: typeof r.source_tier === "string" ? r.source_tier : null,
      fixture_id: typeof r.fixture_id === "string" ? r.fixture_id : null,
      match_name: typeof r.match_name === "string" ? r.match_name : null,
      reason: typeof r.reason === "string" ? r.reason : null,
      expires_at: typeof r.expires_at === "string" ? r.expires_at : null,
      created_at: typeof r.created_at === "string" ? r.created_at : null,
    })));
  }, [rpc]);

  const excludeLeg = useCallback(
    async (leg: { source_leg_id: string; source_tier?: string; fixture_id?: string | null; match_name?: string; reason?: string }) => {
      setBusy(true);
      try {
        const { error: e } = await rpc("add_perfect10_exclusion", {
          p_source_leg_id: leg.source_leg_id,
          p_source_tier: leg.source_tier ?? null,
          p_fixture_id: leg.fixture_id ?? null,
          p_match_name: leg.match_name ?? null,
          p_reason: leg.reason ?? "Admin exclusion",
          p_expires_at: null,
        });
        if (e) setError(e.message);
        await loadExclusions();
      } finally {
        setBusy(false);
      }
    },
    [rpc, loadExclusions],
  );

  const removeExclusion = useCallback(
    async (id: string) => {
      setBusy(true);
      try {
        const { error: e } = await rpc("remove_perfect10_exclusion", { p_id: id });
        if (e) setError(e.message);
        await loadExclusions();
      } finally {
        setBusy(false);
      }
    },
    [rpc, loadExclusions],
  );

  return {
    busy, logs, exclusions, lastResult, error,
    regenerate, loadLogs, loadExclusions, excludeLeg, removeExclusion,
  };
}
